import { useTranslation } from 'react-i18next';

import { ApiError } from './ApiError';

/**
 * uml/18-api-specification.md §2.5 and NFR04.
 *
 * The error envelope carries two strings and only one of them is for a human:
 * `message` is English and exists so a stack trace can be found in a log,
 * `messageKey` is what the interface renders. `ApiError` says so on the line
 * that assigns it.
 *
 * Sixteen screens rendered `err.message` anyway - `toast.error(err?.message ||
 * t('...'))` - so a French student who failed to book a ride was answered in
 * English by the server's own debugging string. This hook is the one thing a
 * catch block should reach for.
 *
 * Where the screen has room for it, prefer `<RuleRefusal error={...} />`: it
 * does this AND puts the rule number in the console, which is what makes a
 * refusal debuggable six months later.
 */
export function useErrorText(): (error: unknown, fallbackKey?: string) => string {
  const { t } = useTranslation();

  return (error, fallbackKey = 'common.errors.unexpected') =>
    // `messageKey` already falls back per status (401, 403, 404, 429, 5xx and
    // the network failure), so a NetworkError keeps its own sentence here
    // rather than collapsing into the caller's generic one.
    error instanceof ApiError ? t(error.messageKey, error.params) : t(fallbackKey);
}
